// CabinetHistoryTable.tsx — the last N measurement times of ONE cabinet,
// shown under the Dashboard's "Latest Measurements" chips when a chip is
// clicked. Answers "when exactly did this cabinet report, the last few times?",
// which the single age label on the chip can't.
//
// Rows come from the lightweight /cabinet-history endpoint (timestamp +
// cabinet_status only). Per snapshot we display the cabinet's OWN
// `last_measurement`, not the frame timestamp: on a frame where the cabinet
// didn't deliver, the pipeline carries its previous value forward, so the frame
// time would claim a measurement that never happened. Those carried-forward
// rows are marked instead of being shown as fresh data.
import { useTranslation } from 'react-i18next';
import type { CabinetHistoryEntry } from 'src/types';
import { parseUtcTimestamp } from 'src/utils/timestamps';
import './CabinetHistoryTable.css';

interface CabinetHistoryTableProps {
  // The cabinet key being expanded ('' for a single-cabinet installation).
  cabinetNr: string;
  // Label shown in the table caption — same wording as the chip.
  cabinetLabel: string;
  entries: CabinetHistoryEntry[] | undefined;
  isPending: boolean;
  isError: boolean;
}

const CabinetHistoryTable = ({
  cabinetNr,
  cabinetLabel,
  entries,
  isPending,
  isError,
}: CabinetHistoryTableProps) => {
  const { t, i18n } = useTranslation();

  if (isPending) {
    return <p className="cabinet-history-note">{t('Loading...')}</p>;
  }
  if (isError) {
    return <p className="cabinet-history-note">{t('dashboard.history.error')}</p>;
  }

  // One row per snapshot, reduced to this cabinet's own measurement time.
  // Snapshots that predate the cabinet_status feature (or that never saw this
  // cabinet) carry no entry for it and are dropped rather than shown blank.
  const rows = (entries ?? [])
    .map((entry) => {
      const status = entry.cabinet_status?.[cabinetNr];
      const measuredAt = parseUtcTimestamp(status?.last_measurement);
      return measuredAt ? { measuredAt, stale: status?.stale === true } : null;
    })
    .filter((row): row is { measuredAt: Date; stale: boolean } => row !== null);

  // De-duplicate: consecutive frames that carried the same cabinet reading
  // forward would otherwise repeat one timestamp down the table.
  const unique = rows.filter(
    (row, i) => i === 0 || row.measuredAt.getTime() !== rows[i - 1].measuredAt.getTime()
  );

  if (unique.length === 0) {
    return <p className="cabinet-history-note">{t('No measurements available')}</p>;
  }

  return (
    <table className="cabinet-history-table" data-testid="cabinet-history-table">
      <caption className="cabinet-history-caption">{cabinetLabel}</caption>
      <tbody>
        {unique.map(({ measuredAt, stale }) => (
          <tr key={measuredAt.toISOString()}>
            <td className="cabinet-history-time">
              {measuredAt.toLocaleString(i18n.language, {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </td>
            <td className="cabinet-history-flag">
              {stale ? t('dashboard.history.carriedForward') : ''}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default CabinetHistoryTable;
